import posthog from 'posthog-js'

import { STORAGE_KEYS } from '@/lib/storageKeys'
import type { ReferralContext } from '@/types/referralAttribution.types'

const EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

class ReferralAttributionService {
  private extractClickIds(
    searchParams: URLSearchParams
  ): Record<string, string> {
    const entities = searchParams.entries()

    return Array.from(entities).reduce((clickIds, [key, value]) => {
      if (key.endsWith('clid') || key.endsWith('clkid')) {
        clickIds = {
          ...clickIds,
          [key]: value,
        }
      }

      return clickIds
    }, {})
  }

  private hasAttribution(context: ReferralContext): boolean {
    const hasUtm = [
      context.utmSource,
      context.utmMedium,
      context.utmCampaign,
      context.utmTerm,
      context.utmContent,
    ].some((value) => value !== null)
    const hasClickIds = Object.keys(context.clickIds).length > 0

    return hasUtm || hasClickIds || !!context.originalReferrer
  }

  private isExternalReferrer(): boolean {
    if (!document.referrer) {
      return false
    }

    try {
      return new URL(document.referrer).origin !== window.location.origin
    } catch {
      return false
    }
  }

  private currentUrlHasAttribution(): boolean {
    const searchParams = new URLSearchParams(window.location.search)
    const hasUtm = UTM_PARAMS.some((param) => searchParams.has(param))
    const hasClickIds = Array.from(searchParams.keys()).some(
      (key) => key.endsWith('clid') || key.endsWith('clkid')
    )

    return hasUtm || hasClickIds || this.isExternalReferrer()
  }

  private sanitizeReferrer(referrer: string): string {
    try {
      const url = new URL(referrer)
      return `${url.origin}${url.pathname}`
    } catch {
      return referrer
    }
  }

  captureIfNeeded(): void {
    const existing = this.getContext()
    const currentHasAttribution = this.currentUrlHasAttribution()

    if (existing && (this.hasAttribution(existing) || !currentHasAttribution)) {
      return
    }

    const searchParams = new URLSearchParams(window.location.search)
    const rawReferrer = this.isExternalReferrer() ? document.referrer : null

    const context: ReferralContext = {
      originalReferrer: rawReferrer ? this.sanitizeReferrer(rawReferrer) : null,
      utmSource: searchParams.get('utm_source'),
      utmMedium: searchParams.get('utm_medium'),
      utmCampaign: searchParams.get('utm_campaign'),
      utmTerm: searchParams.get('utm_term'),
      utmContent: searchParams.get('utm_content'),
      clickIds: this.extractClickIds(searchParams),
      landingPage: window.location.pathname,
      capturedAt: Date.now(),
    }

    localStorage.setItem(
      STORAGE_KEYS.REFERRAL_ATTRIBUTION,
      JSON.stringify(context)
    )
  }

  getContext(): ReferralContext | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.REFERRAL_ATTRIBUTION)

      if (!stored) {
        return null
      }

      const parsed = JSON.parse(stored)

      if (
        typeof parsed.capturedAt !== 'number' ||
        typeof parsed.landingPage !== 'string' ||
        typeof parsed.clickIds !== 'object' ||
        parsed.clickIds === null
      ) {
        this.clear()
        return null
      }

      const context = parsed as ReferralContext
      const isExpired = Date.now() - context.capturedAt > EXPIRY_MS

      if (isExpired) {
        this.clear()
        return null
      }

      return context
    } catch {
      this.clear()
      return null
    }
  }

  applyToPostHog(): void {
    const properties = this.getEventProperties()

    if (Object.keys(properties).length === 0) {
      return
    }

    posthog.setPersonProperties(properties)
  }

  getEventProperties(): Record<string, string> {
    const context = this.getContext()

    if (!context) {
      return {}
    }

    const properties: Record<string, string> = {}

    if (context.originalReferrer) {
      properties['$initial_referrer_override'] = context.originalReferrer
    }

    const utmMapping: Array<
      [(typeof UTM_PARAMS)[number], keyof ReferralContext]
    > = [
      ['utm_source', 'utmSource'],
      ['utm_medium', 'utmMedium'],
      ['utm_campaign', 'utmCampaign'],
      ['utm_term', 'utmTerm'],
      ['utm_content', 'utmContent'],
    ]

    for (const [propertyName, contextKey] of utmMapping) {
      const value = context[contextKey]

      if (typeof value === 'string') {
        properties[propertyName] = value
      }
    }

    return {
      ...properties,
      ...context.clickIds,
      landing_page: context.landingPage,
    }
  }

  clear(): void {
    localStorage.removeItem(STORAGE_KEYS.REFERRAL_ATTRIBUTION)
  }
}

const referralAttributionService = new ReferralAttributionService()

export { ReferralAttributionService, referralAttributionService }
